import { Box, styled } from '@mui/material';
import React, { type ReactNode } from 'react';

import { LoanExampleText } from '@/components/LoanExampleText/index.ts';

import { Boat } from '../../../_common/Boat.tsx';
import { Cloud1 } from '../../../_common/Cloud1.tsx';
import { Cloud2 } from '../../../_common/Cloud2.tsx';
import { House } from '../_assets/House.tsx';
import { Mountains } from '../_assets/Mountains.tsx';
import { Splash } from '../_assets/Splash.tsx';
import { SunCloud } from '../_assets/SunCloud.tsx';

const Root = styled('div')({
  backgroundColor: '#D1ECFE',
  minHeight: 'calc(100vh - var(--top-bar-height))',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'clip',
});

const Sky = styled('div')(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  isolation: 'isolate',
}));

const Water = styled('div')(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#74C6FD',
  minHeight: '100px',
  marginTop: 'auto',

  [theme.breakpoints.up('lg')]: {
    minHeight: '120px',
  },
}));

const SandFooter = styled('div')(({ theme }) => ({
  backgroundColor: '#FFE4B0',
  color: '#00000099',
  padding: theme.spacing(3),
  borderTop: '5px solid #FFFFFF',
  paddingTop: theme.spacing(5),
  display: 'flex',

  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(3),
  },
}));

export type TopSectionBackgroundProps = {
  children?: ReactNode;
  sunPosition?: 'low' | 'high';
};

export const TopSectionBackground: React.FC<TopSectionBackgroundProps> = ({
  children,
  sunPosition = 'high',
}) => {
  return (
    <Root>
      <Sky>
        {children}

        {/* Small on the top */}
        <Cloud1
          sx={{
            position: 'absolute',
            zIndex: -1,
            display: { xs: 'none', sm: 'initial' },
            scale: { xs: '0.9', md: '1', lg: '1.2' },
            top: { xs: 125, md: '20%', lg: '15%' },
            left: { xs: 70, md: '23%', lg: '25%', xl: '35%' },
          }}
        />

        {/* Big white on the left */}
        <Cloud2
          sx={{
            position: 'absolute',
            zIndex: -1,
            scale: { sm: '1.1', md: '1.3', lg: '1.8' },
            top: { xs: '45%' },
            left: { xs: '-30px', md: '5%', lg: '10%', xl: '15%' },
          }}
        />

        <SunCloud
          sx={{
            position: 'absolute',
            zIndex: -1,
            width: { xs: 110, sm: 150, md: 180, lg: 220 },
            top: {
              xs: sunPosition === 'high' ? 16 : 200,
              sm: 32,
              md: '8%',
              lg: '20%',
            },
            right: {
              xs: sunPosition === 'high' ? '8%' : '-10%',
              lg: '5%',
              xl: '15%',
            },
          }}
        />

        {/* Above mountains */}
        <Cloud1
          sx={{
            position: 'absolute',
            zIndex: -1,
            scale: { xs: '0.7', sm: '0.9', md: '1', lg: '1.1' },
            bottom: { xs: 30 },
            right: { xs: 60, sm: 110, md: 180, lg: 310 },
          }}
        />

        <Mountains
          sx={{
            position: 'absolute',
            width: { xs: 194, sm: 300, md: 400, lg: 500 },
            bottom: 0,
            right: { xs: 36, sm: 50, md: 100, lg: 200 },
          }}
        />
      </Sky>
      <Water>
        <Splash
          sx={{
            position: 'absolute',
            width: 40,
            left: { xs: 0, lg: 20, xl: 75 },
            top: { xs: 64, md: 70 },
          }}
        />
        <Boat
          sx={{
            position: 'absolute',
            left: { xs: 20, md: 30, lg: 50, xl: 100 },
            top: 25,
            height: 40,
            scale: { md: '1.2' },
          }}
        />
        <House
          sx={{
            position: 'absolute',
            width: 200,
            right: 0,
            bottom: { xs: -30, lg: -20 },
            scale: { md: '1.5', lg: '1.7' },
          }}
        />
      </Water>
      <SandFooter className="typography-v1-caption1">
        <Box
          sx={{
            maxWidth: '1500px',
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box
            sx={{
              maxWidth: '74ch',
            }}
          >
            <LoanExampleText />
          </Box>
        </Box>
      </SandFooter>
    </Root>
  );
};
